import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useOutlet } from "@/contexts/OutletContext";
import { fmt } from "@/lib/format";
import {
  Plus, Trash, PencilSimple, FloppyDisk, X, Gear, Receipt,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

const RANGES = [
  { key: "today", label: "Today" },
  { key: "yesterday", label: "Yesterday" },
  { key: "7d", label: "7d" },
  { key: "30d", label: "30d" },
  { key: "mtd", label: "MTD" },
];

const todayIST = () => {
  // IST wall-clock date as YYYY-MM-DD
  const now = new Date(Date.now() + (330 - new Date().getTimezoneOffset()) * 60000);
  return now.toISOString().slice(0, 10);
};

const blankExpense = () => ({
  date: todayIST(),
  category: "",
  amount: "",
  note: "",
});

export default function Expenses() {
  const { staff } = useAuth();
  const { outlets, activeOutletId } = useOutlet();
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [period, setPeriod] = useState("30d");
  const [editing, setEditing] = useState(null);
  const [showCats, setShowCats] = useState(false);
  const [catEditing, setCatEditing] = useState(null);
  const [newCatName, setNewCatName] = useState("");
  const [loading, setLoading] = useState(false);

  const isSuper = staff?.role === "super_admin";
  const isAdmin = isSuper || staff?.role === "admin";

  const load = async () => {
    setLoading(true);
    try {
      const [ex, cats] = await Promise.all([
        api.get(`/expenses?period=${period}`),
        api.get("/expense-categories"),
      ]);
      setExpenses(ex.data);
      setCategories(cats.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [period, activeOutletId]);

  const totals = useMemo(() => {
    const total = expenses.reduce((s, e) => s + (e.amount || 0), 0);
    const byCat = expenses.reduce((m, e) => {
      m[e.category] = (m[e.category] || 0) + (e.amount || 0);
      return m;
    }, {});
    return {
      total,
      byCat: Object.entries(byCat).sort((a, b) => b[1] - a[1]),
    };
  }, [expenses]);

  const outletName = (id) => outlets.find((o) => o.id === id)?.name || "—";

  const startNew = () => {
    if (!categories.length) {
      toast.error("Add an expense category first");
      return;
    }
    setEditing({ ...blankExpense(), category: categories[0].name });
  };

  const save = async () => {
    if (!editing.category || !editing.amount) {
      toast.error("Category and amount required");
      return;
    }
    const amt = parseFloat(editing.amount);
    if (isNaN(amt) || amt <= 0) {
      toast.error("Amount must be positive");
      return;
    }
    try {
      if (editing.id) {
        await api.patch(`/expenses/${editing.id}`, {
          date: editing.date,
          category: editing.category,
          amount: amt,
          note: editing.note,
        });
        toast.success("Updated");
      } else {
        const payload = {
          date: editing.date,
          category: editing.category,
          amount: amt,
          note: editing.note,
        };
        if (isSuper && activeOutletId) payload.outlet_id = activeOutletId;
        await api.post("/expenses", payload);
        toast.success("Expense added");
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this expense?")) return;
    try {
      await api.delete(`/expenses/${id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const saveCategory = async () => {
    const name = (catEditing?.name || newCatName || "").trim();
    if (!name) { toast.error("Name required"); return; }
    try {
      if (catEditing?.id) {
        await api.patch(`/expense-categories/${catEditing.id}`, {
          name,
          sort_order: catEditing.sort_order || 0,
          active: catEditing.active ?? true,
        });
      } else {
        await api.post("/expense-categories", { name, sort_order: categories.length });
      }
      setCatEditing(null);
      setNewCatName("");
      const cats = await api.get("/expense-categories");
      setCategories(cats.data);
      toast.success("Saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  const deleteCategory = async (id) => {
    if (!window.confirm("Delete this category? Past expenses will keep the name.")) return;
    try {
      await api.delete(`/expense-categories/${id}`);
      const cats = await api.get("/expense-categories");
      setCategories(cats.data);
      toast.success("Deleted");
    } catch (err) {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="h-screen flex flex-col">
      <div className="p-6 bg-white border-b border-boba-stoneLine">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="font-display text-3xl font-bold" data-testid="expenses-title">Expenses</h1>
            <p className="text-sm text-boba-inkSoft mt-1">Daily operational costs for your outlet (IST)</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex gap-1 bg-boba-cream rounded-md p-1 border border-boba-stoneLine">
              {RANGES.map((r) => (
                <button
                  key={r.key}
                  onClick={() => setPeriod(r.key)}
                  className={`px-3 h-8 rounded text-xs font-mono uppercase tracking-wider ${period === r.key ? "bg-white text-boba-ink shadow-sm" : "text-boba-inkSoft"}`}
                  data-testid={`range-${r.key}`}
                >{r.label}</button>
              ))}
            </div>
            {isAdmin && (
              <button
                onClick={() => setShowCats(true)}
                className="h-10 px-3 border border-boba-stoneLine rounded-md hover:bg-stone-50 flex items-center gap-2 text-sm"
                data-testid="manage-categories-btn"
              >
                <Gear size={16} /> Categories
              </button>
            )}
            <button
              onClick={startNew}
              className="h-10 px-4 bg-boba-matcha hover:bg-boba-matchaDark text-white rounded-md font-medium flex items-center gap-2"
              data-testid="new-expense-btn"
            >
              <Plus size={16} weight="bold" /> Add Expense
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-5">
          <div className="bg-boba-cream border border-boba-stoneLine rounded-md p-4">
            <div className="text-[11px] uppercase tracking-widest text-boba-inkSoft font-mono">Total</div>
            <div className="font-mono text-2xl font-bold mt-2" data-testid="expenses-total">{fmt(totals.total)}</div>
            <div className="text-xs text-boba-inkSoft mt-1">{expenses.length} entries</div>
          </div>
          <div className="bg-boba-cream border border-boba-stoneLine rounded-md p-4 md:col-span-2">
            <div className="text-[11px] uppercase tracking-widest text-boba-inkSoft font-mono">Top categories</div>
            <div className="mt-2 space-y-1.5 max-h-24 overflow-auto">
              {totals.byCat.length === 0 && <div className="text-sm text-boba-inkSoft">No expenses yet</div>}
              {totals.byCat.slice(0, 4).map(([c, v]) => (
                <div key={c} className="flex justify-between text-sm">
                  <span className="text-boba-ink">{c}</span>
                  <span className="font-mono font-semibold">{fmt(v)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-6">
        {loading && expenses.length === 0 && <div className="text-center text-stone-500 py-12">Loading…</div>}
        {!loading && expenses.length === 0 && (
          <div className="text-center text-stone-500 py-12">
            <Receipt size={40} className="mx-auto mb-2 text-stone-300" />
            No expenses recorded for this period.
          </div>
        )}
        {expenses.length > 0 && (
          <div className="bg-white border border-boba-stoneLine rounded-md overflow-hidden">
            <table className="w-full text-sm" data-testid="expenses-table">
              <thead className="bg-boba-cream text-boba-inkSoft text-[11px] uppercase tracking-widest font-mono">
                <tr>
                  <th className="px-4 py-3 text-left">Date</th>
                  <th className="px-4 py-3 text-left">Category</th>
                  {isSuper && <th className="px-4 py-3 text-left">Outlet</th>}
                  <th className="px-4 py-3 text-left">Added by</th>
                  <th className="px-4 py-3 text-left">Note</th>
                  <th className="px-4 py-3 text-right">Amount</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {expenses.map((e) => (
                  <tr key={e.id} className="border-t border-boba-stoneLine hover:bg-stone-50" data-testid={`expense-row-${e.id}`}>
                    <td className="px-4 py-3 font-mono text-xs">{e.date}</td>
                    <td className="px-4 py-3">{e.category}</td>
                    {isSuper && <td className="px-4 py-3 text-boba-inkSoft">{outletName(e.outlet_id)}</td>}
                    <td className="px-4 py-3 text-boba-inkSoft">{e.created_by_name || "—"}</td>
                    <td className="px-4 py-3 text-boba-inkSoft max-w-[220px] truncate" title={e.note}>{e.note || "—"}</td>
                    <td className="px-4 py-3 text-right font-mono font-semibold">{fmt(e.amount)}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex gap-1 justify-end">
                        <button
                          onClick={() => setEditing({ ...e, amount: String(e.amount) })}
                          className="p-1.5 hover:bg-stone-100 rounded"
                          data-testid={`edit-expense-${e.id}`}
                          title="Edit"
                        ><PencilSimple size={16} /></button>
                        <button
                          onClick={() => remove(e.id)}
                          className="p-1.5 hover:bg-red-50 text-red-600 rounded"
                          data-testid={`delete-expense-${e.id}`}
                          title="Delete"
                        ><Trash size={16} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add / Edit expense dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-md" data-testid="expense-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">
              {editing?.id ? "Edit Expense" : "New Expense"}
            </DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs uppercase tracking-widest text-boba-inkSoft font-mono">Date</label>
                  <input
                    type="date"
                    value={editing.date}
                    onChange={(e) => setEditing({ ...editing, date: e.target.value })}
                    className="w-full mt-1 h-10 px-3 border border-boba-stoneLine rounded-md font-mono"
                    data-testid="expense-date"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-boba-inkSoft font-mono">Amount (₹)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={editing.amount}
                    onChange={(e) => setEditing({ ...editing, amount: e.target.value })}
                    className="w-full mt-1 h-10 px-3 border border-boba-stoneLine rounded-md font-mono"
                    data-testid="expense-amount"
                    placeholder="0.00"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-boba-inkSoft font-mono">Category</label>
                <select
                  value={editing.category}
                  onChange={(e) => setEditing({ ...editing, category: e.target.value })}
                  className="w-full mt-1 h-10 px-2 border border-boba-stoneLine rounded-md bg-white"
                  data-testid="expense-category"
                >
                  {categories.map((c) => (
                    <option key={c.id} value={c.name}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-boba-inkSoft font-mono">Note (optional)</label>
                <input
                  value={editing.note}
                  onChange={(e) => setEditing({ ...editing, note: e.target.value })}
                  className="w-full mt-1 h-10 px-3 border border-boba-stoneLine rounded-md"
                  data-testid="expense-note"
                  placeholder="e.g. Amul 5L pack"
                />
              </div>
              {isSuper && !editing.id && (
                <div className="text-xs text-boba-inkSoft bg-boba-cream border border-boba-stoneLine rounded p-2">
                  Will be recorded against active outlet: <b>{outletName(activeOutletId) || "select in sidebar"}</b>
                </div>
              )}
              <div className="flex justify-end gap-2 pt-3 border-t border-boba-stoneLine">
                <button onClick={() => setEditing(null)} className="h-10 px-4 border border-boba-stoneLine rounded-md hover:bg-stone-50">Cancel</button>
                <button onClick={save} className="h-10 px-4 bg-boba-matcha hover:bg-boba-matchaDark text-white rounded-md font-medium flex items-center gap-2" data-testid="expense-save">
                  <FloppyDisk size={16} weight="bold" /> Save
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Manage categories dialog */}
      <Dialog open={showCats} onOpenChange={setShowCats}>
        <DialogContent className="max-w-md" data-testid="categories-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Expense Categories</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex gap-2">
              <input
                value={newCatName}
                onChange={(e) => setNewCatName(e.target.value)}
                placeholder="New category name"
                className="flex-1 h-10 px-3 border border-boba-stoneLine rounded-md"
                data-testid="new-category-input"
              />
              <button
                onClick={() => saveCategory()}
                disabled={!newCatName.trim()}
                className="h-10 px-4 bg-boba-matcha hover:bg-boba-matchaDark text-white rounded-md font-medium disabled:opacity-50"
                data-testid="add-category-btn"
              >Add</button>
            </div>
            <div className="max-h-72 overflow-auto space-y-1">
              {categories.map((c) => (
                <div key={c.id} className="flex items-center gap-2 p-2 border border-boba-stoneLine rounded" data-testid={`category-row-${c.id}`}>
                  {catEditing?.id === c.id ? (
                    <>
                      <input
                        value={catEditing.name}
                        onChange={(e) => setCatEditing({ ...catEditing, name: e.target.value })}
                        className="flex-1 h-9 px-2 border border-boba-stoneLine rounded"
                        autoFocus
                      />
                      <button onClick={saveCategory} className="p-1.5 hover:bg-stone-100 rounded"><FloppyDisk size={16} /></button>
                      <button onClick={() => setCatEditing(null)} className="p-1.5 hover:bg-stone-100 rounded"><X size={16} /></button>
                    </>
                  ) : (
                    <>
                      <div className="flex-1 text-sm">{c.name}</div>
                      <button onClick={() => setCatEditing(c)} className="p-1.5 hover:bg-stone-100 rounded"><PencilSimple size={14} /></button>
                      <button onClick={() => deleteCategory(c.id)} className="p-1.5 hover:bg-red-50 text-red-600 rounded"><Trash size={14} /></button>
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
